import React, { useEffect, useMemo, useState } from "react";
import axios from "../../API/axios";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { useTranslation } from "react-i18next";
import { Portal } from "../../components/Portal";
import "../../styles/recruiterFeatures.css";

const DEFAULT_DIMS = [
  { key: "Skills", label: "Skills", dimensionIndex: 1, weight: 0.3, mandatory: true },
  { key: "Education", label: "Education", dimensionIndex: 2, weight: 0.15, mandatory: false },
  { key: "Languages", label: "Languages", dimensionIndex: 3, weight: 0.1, mandatory: false },
  { key: "Experience", label: "Experience", dimensionIndex: 4, weight: 0.2, mandatory: true },
  { key: "Location", label: "Location", dimensionIndex: 5, weight: 0.1, mandatory: false },
  { key: "Industry", label: "Industry", dimensionIndex: 6, weight: 0.1, mandatory: false },
  { key: "SalaryFit", label: "Salary fit", dimensionIndex: 7, weight: 0.05, mandatory: false },
];

/**
 * Job match weights modal — Skills, Education, Languages, Experience, Location, Industry, Salary fit.
 * Auto-normalizes to 1.00 on save.
 */
const JobMatchWeightsModal = ({ show, jobId, compUid, onClose, onSaved }) => {
  const { t } = useTranslation();
  const sessionId = localStorage.getItem("sessionId");
  const [dimensions, setDimensions] = useState(DEFAULT_DIMS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [healthIssues, setHealthIssues] = useState([]);
  const [healthy, setHealthy] = useState(true);

  const totalWeight = useMemo(
    () => dimensions.reduce((sum, d) => sum + (Number(d.weight) || 0), 0),
    [dimensions]
  );
  const isNormalized = Math.abs(totalWeight - 1) <= 0.001;
  const hasMandatoryZero = dimensions.some((d) => d.mandatory && !(Number(d.weight) > 0));

  useEffect(() => {
    if (!show || !jobId) return;
    setLoading(true);
    setError("");
    setInfo("");
    axios
      .get(`Match/GetJobMatchWeights?DeviceType=Web&JobId=${jobId}`)
      .then((res) => {
        const data = res?.data?.responseData;
        const dims = data?.dimensions || data?.Dimensions;
        setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
        setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
        if (Array.isArray(dims) && dims.length) {
          setDimensions(
            dims.map((d, i) => ({
              key: d.key || d.Key,
              label: d.label || d.Label,
              dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
              weight: Number(d.weight ?? d.Weight ?? 0),
              mandatory: Boolean(d.mandatory ?? d.Mandatory),
            }))
          );
        } else {
          setDimensions(DEFAULT_DIMS);
        }
      })
      .catch(() => setError(t("Recruiter.WeightsLoadFailed")))
      .finally(() => setLoading(false));
  }, [show, jobId, t]);

  const updateDim = (key, patch) => {
    setDimensions((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const handleSave = async () => {
    if (hasMandatoryZero) {
      setError(t("Recruiter.MandatoryWeightRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/SetJobMatchWeights", {
        deviceType: "Web",
        sessionId,
        jobId,
        dimensions,
      });
      const data = res?.data?.responseData;
      if (data?.dimensions || data?.Dimensions) {
        const dims = data.dimensions || data.Dimensions;
        setDimensions(
          dims.map((d, i) => ({
            key: d.key || d.Key,
            label: d.label || d.Label,
            dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
            weight: Number(d.weight ?? d.Weight ?? 0),
            mandatory: Boolean(d.mandatory ?? d.Mandatory),
          }))
        );
      }
      setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
      setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.WeightsSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const applyCompanyTemplate = async () => {
    if (!compUid) {
      setError(t("Recruiter.CompanyRequiredTemplate"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/ApplyMatchWeightTemplate", {
        deviceType: "Web",
        sessionId,
        compUid,
        jobIds: jobId ? [jobId] : null,
        onlyMissing: false,
      });
      setInfo(res?.data?.message || t("Recruiter.TemplateApplied"));
      const reload = await axios.get(`Match/GetJobMatchWeights?DeviceType=Web&JobId=${jobId}`);
      const data = reload?.data?.responseData;
      const dims = data?.dimensions || data?.Dimensions;
      if (Array.isArray(dims) && dims.length) {
        setDimensions(
          dims.map((d, i) => ({
            key: d.key || d.Key,
            label: d.label || d.Label,
            dimensionIndex: d.dimensionIndex || d.DimensionIndex || i + 1,
            weight: Number(d.weight ?? d.Weight ?? 0),
            mandatory: Boolean(d.mandatory ?? d.Mandatory),
          }))
        );
      }
      setHealthy(data?.isHealthy ?? data?.IsHealthy ?? true);
      setHealthIssues(data?.healthIssues || data?.HealthIssues || []);
      onSaved?.(data);
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.TemplateApplyFailed"));
    } finally {
      setSaving(false);
    }
  };

  const saveAsCompanyTemplate = async () => {
    if (!compUid) {
      setError(t("Recruiter.CompanyRequiredTemplate"));
      return;
    }
    if (hasMandatoryZero) {
      setError(t("Recruiter.MandatoryWeightRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.post("Match/SetCompanyMatchWeights", {
        deviceType: "Web",
        sessionId,
        compUid,
        dimensions,
      });
      setInfo(res?.data?.message || t("Recruiter.TemplateSaved"));
    } catch (e) {
      setError(e?.response?.data?.message || t("Recruiter.TemplateSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Portal layer="modal">
      <div className="modal fade show postAdd wlj-overlay-modal" style={{ display: "flex" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content d-flex flex-column">
            <div className="setting-header d-flex justify-content-between align-items-center">
              <h6>{t("Common.JobMatchWeights")}</h6>
              <CloseSharpIcon className="material-symbols-sharp me-2 pointer" onClick={onClose} />
            </div>

            <div className="wlj-modal-body p-3">
              <p className="small text-muted mb-2">{t("Recruiter.WeightsHint")}</p>

              <div className="wlj-weight-total">
                {t("Recruiter.TotalWeight")}: <strong>{totalWeight.toFixed(2)}</strong>
                {" — "}
                {isNormalized ? t("Recruiter.AlreadyNormalized") : t("Recruiter.WillNormalize")}
              </div>

              {(!healthy || healthIssues.length > 0 || hasMandatoryZero) && (
                <div className="alert alert-danger py-2 small" role="alert">
                  {(healthIssues.length
                    ? healthIssues
                    : [t("Recruiter.MandatoryWeightHealth")]
                  ).join(" ")}
                </div>
              )}
              {info && (
                <div className="alert alert-success py-2 small" role="status">
                  {info}
                </div>
              )}

              {loading ? (
                <p>{t("Loading")}</p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {dimensions.map((d) => (
                    <div key={d.key} className="row g-2 align-items-center">
                      <div className="col">
                        <div className="fw-semibold">{d.label}</div>
                        <div className="small text-muted">
                          {t("Recruiter.DimensionN", { n: d.dimensionIndex })}
                        </div>
                      </div>
                      <div className="col-auto">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="form-control"
                          style={{ width: 96 }}
                          value={d.weight}
                          aria-label={`${d.label} ${t("Recruiter.Weight")}`}
                          onChange={(e) => updateDim(d.key, { weight: Number(e.target.value) })}
                        />
                      </div>
                      <div className="col-auto">
                        <label className="d-flex align-items-center gap-2 mb-0 small">
                          <input
                            type="checkbox"
                            checked={d.mandatory}
                            onChange={(e) => updateDim(d.key, { mandatory: e.target.checked })}
                          />
                          {t("Recruiter.Mandatory")}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {error && <p className="text-danger small mt-2 mb-0">{error}</p>}
            </div>

            <div className="wlj-modal-footer d-flex flex-nowrap align-items-center justify-content-end gap-2">
              <button type="button" className="wljDangerBtn" onClick={onClose}>
                {t("Common.Close")}
              </button>
              {compUid && (
                <>
                  <button
                    type="button"
                    className="wljSecondaryBtn"
                    disabled={saving || loading}
                    onClick={applyCompanyTemplate}
                  >
                    {t("Recruiter.ApplyCompanyTemplate")}
                  </button>
                  <button
                    type="button"
                    className="wljSecondaryBtn"
                    disabled={saving || loading}
                    onClick={saveAsCompanyTemplate}
                  >
                    {t("Recruiter.SaveAsCompanyTemplate")}
                  </button>
                </>
              )}
              <button type="button" className="mainBtn1" disabled={saving || loading} onClick={handleSave}>
                {saving ? t("Recruiter.Saving") : t("Newsfeed.Save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default JobMatchWeightsModal;
